import type { BlockStateDefinition } from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type { BlockType, ComponentTypeUnionType } from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import { MessageInputMethods } from '@unifyapps/defs/blocks/MessageInput/types';
import type {
  MessageInputBlockRef,
  MessageInputBlockStateType,
  MessageInputComponentType,
} from '@unifyapps/defs/blocks/MessageInput/types';
import { lazy } from 'react';
import type { Action } from '@unifyapps/defs/types/action';
import _map from 'lodash/map';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { ENTIRE_ENTITY } from '@unifyapps/carbon/no-code/dependency/const';

const importMessageInputComponent = () =>
  import(
    /* webpackChunkName: "message-input-block" */
    './MessageInput'
  );

const MessageInput = lazy(importMessageInputComponent);

class MessageInputBlockStateDefinition implements BlockStateDefinition {
  get type(): ComponentTypeUnionType {
    return 'MessageInput';
  }

  useBlockRef?: boolean | undefined = true;

  get initialStateGetter() {
    return (block: BlockType) => ({
      id: block.id,
      ..._pick(block.component as MessageInputComponentType, [
        'content',
        'appearance',
        'componentType',
      ]),
      value: '',
    });
  }

  getEventTargetIds(block: BlockType<MessageInputComponentType>) {
    return _map(block.component.content.actions, (action) => action.id);
  }

  getBlockControlMethods() {
    return [
      {
        id: MessageInputMethods.SetValue,
        label: 'Set Value',
        method: (blockState: MessageInputBlockStateType, action: Action) => {
          const { methodPayload } = action.payload as {
            methodPayload: { content: string | undefined };
          };

          return { ...blockState, value: methodPayload.content };
        },
      },
      {
        id: MessageInputMethods.StartDictation,
        label: 'Start Dictation',
        method: (
          blockState: MessageInputBlockStateType,
          action: Action,
          blockRef: MessageInputBlockRef,
        ) => {
          blockRef.startDictation?.();
          return Promise.resolve();
        },
      },
      {
        id: MessageInputMethods.StopDictation,
        label: 'Stop Dictation',
        method: (
          blockState: MessageInputBlockStateType,
          action: Action,
          blockRef: MessageInputBlockRef,
        ) => {
          blockRef.stopDictation?.();
          return Promise.resolve();
        },
      },
      {
        id: MessageInputMethods.Reset,
        label: 'Reset',
        method: (
          blockState: MessageInputBlockStateType,
          action: Action,
          blockRef: MessageInputBlockRef,
        ) => {
          blockRef.reset?.();
          return Promise.resolve({
            ...blockState,
            value: undefined,
            attachments: [],
            content: {
              ...blockState.content,
              autoFocus: false,
            },
          });
        },
      },
    ];
  }

  getImplicitDependencies(
    block: BlockType<MessageInputComponentType>,
  ): DependsOnType[] | undefined {
    const chatId = block.component.content.chatId;
    if (!chatId) return;

    return [
      {
        id: chatId,
        p: [ENTIRE_ENTITY],
      },
    ];
  }

  getComponent() {
    return MessageInput;
  }

  getKeysToOmitInComputation() {
    return ['value'];
  }
}

export default MessageInputBlockStateDefinition;
